export const enum Status {
    Pending = 'Pending',
    Done = 'Done',
    Error = 'Error',
}

interface Item {
    src: string;
    resolve?: () => void;
}

export interface Downloader {
    (src: string): Promise<void>;
}

const defaultDownloader: Downloader = (src: string) => {
    return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = e => reject(e);
        img.src = src;
    });
};

export class PriorityImageDownloader {
    queue: Item[][] = [[]];
    pending = 0;
    pendingLimit: number;
    downloader: Downloader;
    timeout?: number;

    constructor(pendingLimit: number, downloader = defaultDownloader) {
        this.pendingLimit = pendingLimit;
        this.downloader = downloader;
    }

    downloadNow(src: string): Promise<void> | undefined {
        if (this.canDownload()) {
            this.enqueue({ src });
            this.download();
        } else {
            return new Promise(resolve => {
                this.enqueue({ src, resolve });
                this.download();
            });
        }
    }

    downloadWithPriority(src: string, priority: number): Promise<void> {
        return new Promise(resolve => {
            this.enqueue({ src, resolve }, priority);
            if (this.timeout) {
                window.clearTimeout(this.timeout);
            }
            setTimeout(() => {
                delete this.timeout;
                this.download();
            }, 10);
        });
    }

    private canDownload() {
        return this.pending < this.pendingLimit;
    }

    private download() {
        while (this.canDownload()) {
            const item = this.dequeue();
            if (!item) {
                return;
            }

            this.pending++;
            const end = () => {
                this.pending--;
                this.download();
            };
            this.downloader(item.src).then(end, end);
            if (item.resolve) {
                item.resolve();
            }
        }
    }

    private enqueue(item: Item, priority = 0) {
        if (!this.queue[priority]) {
            this.queue[priority] = [];
        }
        this.queue[priority].push(item);
    }

    private dequeue(): Item | undefined {
        for (let i = 0; i < this.queue.length; i++) {
            if (this.queue[i] && this.queue[i].length) {
                return this.queue[i].shift();
            }
        }
    }
}

export default PriorityImageDownloader;
